/**
 * @module AdapterStatusBadge
 * @package @geenius/adapters/react-ant
 * @description Renders the Ant Design tokenized adapter status badge for the React
 * variant. The badge exposes the shared adapter status vocabulary in a compact,
 * themeable visual treatment.
 */

import type { AdapterStatusType } from "@geenius/adapters";
import theme from "antd/lib/theme/index.js";
import type { JSX } from "react";

const statusLabels: Record<AdapterStatusType, string> = {
  connected: "Connected",
  disconnected: "Disconnected",
  error: "Error",
  initializing: "Initializing",
};

/**
 * Props for rendering the React adapter status badge.
 *
 * @property status Shared adapter status vocabulary value to display.
 * @property showLabel Whether the human-readable label should be rendered.
 * @property size Visual size variant for the badge and status dot.
 * @property className Optional class name merged into the badge wrapper.
 * @property style Optional inline styles merged into the badge wrapper.
 */
export interface AdapterStatusBadgeProps {
  status: AdapterStatusType;
  showLabel?: boolean;
  size?: "sm" | "md";
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Renders a compact React badge for the current adapter lifecycle state.
 *
 * @param props Badge props including the status token, optional label visibility, and size variant.
 * @returns A styled badge element that mirrors the shared adapter status taxonomy.
 */
export function AdapterStatusBadge({
  status,
  showLabel = true,
  size = "sm",
  className,
  style,
}: AdapterStatusBadgeProps): JSX.Element {
  const { token } = theme.useToken();

  const getStatusTokens = () => {
    switch (status) {
      case "connected":
        return {
          borderColor: token.colorSuccessBorder,
          backgroundColor: token.colorSuccessBg,
          dotColor: token.colorSuccess,
        };
      case "disconnected":
        return {
          borderColor: token.colorBorder,
          backgroundColor: token.colorBgContainerDisabled,
          dotColor: token.colorTextSecondary,
        };
      case "error":
        return {
          borderColor: token.colorErrorBorder,
          backgroundColor: token.colorErrorBg,
          dotColor: token.colorError,
        };
      default:
        return {
          borderColor: token.colorPrimaryBorder,
          backgroundColor: token.colorPrimaryBg,
          dotColor: token.colorPrimary,
        };
    }
  };

  const statusTokens = getStatusTokens();

  return (
    <span
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: size === "md" ? "6px" : "4px",
        borderRadius: token.borderRadiusLG,
        border: `1px solid ${statusTokens.borderColor}`,
        padding: size === "md" ? "2px 8px" : "0px 6px",
        backgroundColor: statusTokens.backgroundColor,
        color: token.colorText,
        fontWeight: 600,
        textTransform: "uppercase",
        fontSize: size === "md" ? token.fontSizeSM : token.fontSize / 1.15,
        transition: "all 0.2s",
        ...style,
      }}
    >
      <span
        style={{
          height: size === "md" ? "8px" : "6px",
          width: size === "md" ? "8px" : "6px",
          borderRadius: "50%",
          backgroundColor: statusTokens.dotColor,
          animation:
            status === "initializing"
              ? "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite"
              : undefined,
        }}
      />
      {showLabel && <span>{statusLabels[status]}</span>}
    </span>
  );
}
